import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Button,
  makeStyles,
  Typography,
  Box,
  CircularProgress,
} from "@material-ui/core";
import HistoryIcon from "@material-ui/icons/History";
import { ThreeDots } from "react-loader-spinner";
import {
  getCockpitSchemaInputs,
  getCockpitVariants,
  StartCalculations,
  getMergeAnalysis,
  getScenarioPremises,
  getFolders,
  createFolder,
} from "../../viewServices/calls/apiCalls";
import { FolderSelectPopover } from "../../sharedComponents/FolderSelectPopover";
import { toast } from "react-toastify";
import { whiteColor } from "assets/jss/material-dashboard-pro-react.js";
import AuthContext from "../../../../connections/auth/AuthProvider";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import SectionInputVariants from "./SectionInputVariants";
import { flattenSchemaInputs } from "./UtilsFlattenInputs";
import SectionScenarioSpec from "./SectionScenarioSpec";
import SectionModels from "./SectionModels";
import CustomInput from "components/CustomInput/CustomInput.js";
import DialogCopyScenario from "./DialogCopyScenario";

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  modalContainer: {
    display: "flex",
    flexDirection: "column",
    height: "80vh",
    maxHeight: "800px",
  },
  titleSection: {
    // padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginTop: "-30px",
    borderRadius: "5px 5px 0px 0px",
    backgroundColor: "#f5f5f5",
    marginLeft: "-10px",
    marginRight: "-8px",
    paddingTop: 10,
  },
  title: {
    fontSize: "1.1rem",
    marginLeft: theme.spacing(3),
    margin: theme.spacing(0),
    color: theme.palette.text.primary,
    textTransform: "none",
  },
  headerSection: {
    flexShrink: 0,
    padding: "8px 0px",
    // borderBottom: `1px solid ${theme.palette.divider}`,
    marginTop: "-20px", // Adjust to align with the top of the modal
  },
  sectionsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: "calc(100% - 32px)", // 100% minus left + right margins (2 * 16px)
  },
  tableSection: {
    flex: 1,
    overflow: "auto",
    padding: "0px 8px 0px 8px",
  },
  footerSection: {
    flexShrink: 0,
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    marginBottom: "-20px", // Adjust to align with the bottom of the modal
  },
  submitButton: {
    fontSize: "smaller",
    margin: "5px",
    backgroundColor: (props) => props.clientColor,
    color: whiteColor,
    "&:hover": {
      backgroundColor: (props) => props.clientColor,
      transform: "scale(1.07)",
      transition: "transform 0.1s",
    },
    "&:disabled": {
      backgroundColor: (props) => props.clientColor,
      color: whiteColor,
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  checkInfoButton: {
    fontSize: "smaller",
    margin: "5px",
    backgroundColor: (props) => props.clientColor, // Same color as Run Calculations button
    color: whiteColor,
    "&:hover": {
      backgroundColor: (props) => props.clientColor,
      transform: "scale(1.07)",
      transition: "transform 0.1s",
    },
    "&:disabled": {
      backgroundColor: (props) => props.clientColor,
      color: whiteColor,
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
  },
  scenarioNameBox: {
    display: "flex",
    alignItems: "flex-start",
    marginTop: theme.spacing(4),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    gap: theme.spacing(2),
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    width: "calc(100% - 32px)", // 100% minus left + right margins
  },
  scenarioNameInput: {
    flex: "0 0 auto",
    minWidth: "240px",
  },
  scenarioDescriptionInput: {
    flex: "1 1 auto",
    minWidth: "300px",
  },
  usePreviousButton: {
    fontSize: "smaller",
    padding: theme.spacing(1, 2),
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    backgroundColor: (props) => props.clientColor,
    color: whiteColor,
    "&:hover": {
      backgroundColor: (props) => props.clientColor,
      transform: "scale(1.07)",
      transition: "transform 0.1s",
    },
    "&:disabled": {
      backgroundColor: (props) => props.clientColor,
      color: whiteColor,
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
}));

export const ScenarioCreationContent = ({
  onClose,
  scenarios = [],
  isLoading = false,
}) => {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;
  const classes = useStyles({ clientColor });

  const T_PREFIX = "rgm.cockpit.scenarioSelection";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const [schemaInputs, setSchemaInputs] = useState({});
  const [variants, setVariants] = useState({});
  const [scenarioPremises, setScenarioPremises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [scenarioName, setScenarioName] = useState("");
  const [scenarioDescription, setScenarioDescription] = useState("");
  const [calculationMethod, setCalculationMethod] =
    useState("method_valuebased");
  const [scenarioPremisesUsed, setScenarioPremisesUsed] =
    useState("premise_01");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Merge analysis state
  const [mergeAnalysis, setMergeAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [checkInfoCompleted, setCheckInfoCompleted] = useState(false);

  // Copy scenario dialog state
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [highlightedTables, setHighlightedTables] = useState([]);

  // Folder selection state
  const [folders, setFolders] = useState([]);
  const [folderPopoverOpen, setFolderPopoverOpen] = useState(false);
  const [runButtonAnchor, setRunButtonAnchor] = useState(null);

  // Filter FINISHED scenarios from prop (real-time updates)
  const availableScenarios = useMemo(
    () => scenarios.filter((s) => s.status === "FINISHED"),
    [scenarios],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log(
          "📝 ScenarioCreationContent - Fetching schema inputs, variants, and premises",
        );
        const [inputsData, variantsData, premisesData] = await Promise.all([
          getCockpitSchemaInputs(),
          getCockpitVariants(),
          getScenarioPremises(),
        ]);
        setSchemaInputs(inputsData);
        setVariants(variantsData);
        setScenarioPremises(premisesData || []);

        // Set default premise if available
        if (premisesData && premisesData.length > 0) {
          setScenarioPremisesUsed(premisesData[0].premise_key);
        }

        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
        toast.error(t("toast.load_error"));
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const initialSelection = {};
    for (const tableId in variants) {
      if (variants[tableId].length > 0) {
        initialSelection[tableId] = variants[tableId][0].variant_id;
      }
    }
    setSelectedVariants(initialSelection);
  }, [variants]);

  // Fetch folders for folder selection
  useEffect(() => {
    const fetchFolders = async () => {
      try {
        const foldersData = await getFolders();
        setFolders(foldersData);
      } catch (err) {
        console.error("Failed to fetch folders:", err);
      }
    };
    fetchFolders();
  }, []);

  const handleVariantSelect = (tableId, variantId) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [tableId]: variantId,
    }));
    // Reset merge analysis and completion flag when variants change
    setMergeAnalysis(null);
    setCheckInfoCompleted(false);
  };

  // Handlers for copy scenario dialog
  const handleOpenCopyDialog = () => {
    setIsCopyDialogOpen(true);
  };

  const handleCloseCopyDialog = () => {
    setIsCopyDialogOpen(false);
  };

  const handleCopySuccess = (newSelection) => {
    // Apply new selection
    setSelectedVariants(newSelection);

    // Highlight changed tables
    const changedTables = Object.keys(newSelection);
    setHighlightedTables(changedTables);

    // Reset merge analysis and completion flag
    setMergeAnalysis(null);
    setCheckInfoCompleted(false);

    // Remove highlight after 2 seconds
    setTimeout(() => {
      setHighlightedTables([]);
    }, 4000);
  };

  // Open folder selection popover when Run Calculations is clicked
  const handleRunClick = (event) => {
    setFormSubmitted(true);
    if (!scenarioName) {
      return; // Prevent opening popover if scenarioName is empty
    }
    setRunButtonAnchor(event.currentTarget);
    setFolderPopoverOpen(true);
  };

  const handleFolderCancel = () => {
    setFolderPopoverOpen(false);
    setRunButtonAnchor(null);
  };

  const handleFolderConfirm = async (folderId) => {
    setFolderPopoverOpen(false);
    setRunButtonAnchor(null);
    // Wait for real folder ID if it's a temp ID
    const resolvedFolderId = await resolveFolderId(folderId);
    await submitScenario(resolvedFolderId);
  };

  // Track temp to real folder ID mapping and pending promises
  const [folderIdMap, setFolderIdMap] = useState({});
  const [pendingFolderPromises, setPendingFolderPromises] = useState({});

  const handleCreateFolder = (folderName) => {
    // Generate temporary ID for optimistic update (use negative to avoid DB overflow)
    const tempId = -Date.now();

    // Optimistic update - add folder to list immediately
    const newFolder = {
      folder_id: tempId,
      name: folderName,
      parent_folder_id: null,
      color: null,
      subfolders: [],
    };
    setFolders((prev) => [...prev, newFolder]);

    // Create folder in background and store the promise
    const folderPromise = createFolder(folderName, null, null)
      .then((result) => {
        // Update the temp folder with the real ID
        setFolders((prev) =>
          prev.map((f) =>
            f.folder_id === tempId ? { ...f, folder_id: result.folder_id } : f,
          ),
        );
        // Track the mapping so we can update selection if needed
        setFolderIdMap((prev) => ({ ...prev, [tempId]: result.folder_id }));
        return result.folder_id;
      })
      .catch(() => {
        // Rollback on failure
        setFolders((prev) => prev.filter((f) => f.folder_id !== tempId));
        toast.error("Failed to create folder");
        throw new Error("Folder creation failed");
      });

    // Store promise for this temp ID
    setPendingFolderPromises((prev) => ({ ...prev, [tempId]: folderPromise }));

    // Return temp ID immediately for optimistic selection
    return tempId;
  };

  // Resolve folder ID (wait for real ID if temp)
  const resolveFolderId = async (folderId) => {
    // If it's a temp ID (negative), wait for the real ID
    if (folderId < 0) {
      // Check if already resolved
      if (folderIdMap[folderId]) {
        return folderIdMap[folderId];
      }
      // Wait for the pending promise
      if (pendingFolderPromises[folderId]) {
        return await pendingFolderPromises[folderId];
      }
    }
    return folderId;
  };

  const submitScenario = async (folderId) => {
    setIsSubmitting(true);

    const variantsPayload = {};
    for (const tableId in selectedVariants) {
      const variantId = selectedVariants[tableId];
      const variant = variants[tableId]?.find(
        (v) => v.variant_id === variantId,
      );
      variantsPayload[tableId] = {
        id: variantId,
        name: variant ? variant.variant_name : null,
      };
    }

    try {
      const payload = {
        scenario_name: scenarioName,
        scenario_description: scenarioDescription,
        calculation_method: calculationMethod,
        scenario_premises_used: scenarioPremisesUsed,
        selected_variants: variantsPayload,
        folder_id: folderId, // Include folder selection
      };

      const response = await StartCalculations(payload);
      if (response.status === "QUEUED") {
        toast.success(t("toast.calculations_queued_success"));
        onClose(true); // Pass true to indicate refresh is needed
      } else {
        toast.error(t("toast.calculations_queued_failed"));
        onClose();
      }
    } catch (err) {
      if (err === "scenario_already_running") {
        toast.error(t("toast.scenario_already_running"));
      } else {
        toast.error(t("toast.calculations_queued_failed"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckInfo = async () => {
    setIsAnalyzing(true);
    try {
      const response = await getMergeAnalysis(
        selectedVariants,
        calculationMethod,
      );
      setMergeAnalysis(response);
      setCheckInfoCompleted(true); // Enable Run Calculations after check completes
      toast.success(t("toast.merge_analysis_success"));
    } catch (error) {
      console.error("Error getting merge analysis:", error);
      setCheckInfoCompleted(true); // Still enable Run Calculations even if check fails
      toast.error(t("toast.merge_analysis_failed"));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const flattenedData = flattenSchemaInputs(schemaInputs, variants).filter(
    (row) =>
      row.table_id !== "model01" &&
      row.table_id !== "model02" &&
      row.group_level3 !== "disabled",
  );

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <ThreeDots
          height="80"
          width="80"
          radius="9"
          color={clientColor}
          ariaLabel="three-dots-loading"
          wrapperStyle={{}}
          wrapperClassName=""
          visible={true}
        />
      </div>
    );
  }

  if (error) {
    return (
      <Typography color="error">
        {t("error")}: {error.message}
      </Typography>
    );
  }

  return (
    <Box className={classes.modalContainer}>
      {/* // Box as title section */}
      <Box className={classes.titleSection}>
        <Typography variant="h6" className={classes.title}>
          {t("title")}
        </Typography>
      </Box>
      {/* Header Section - Fixed */}
      <Box className={classes.headerSection}>
        <Box className={classes.scenarioNameBox}>
          <Box className={classes.scenarioNameInput}>
            <CustomInput
              labelText={
                checkInfoCompleted && !scenarioName
                  ? `${t("enter_scenario_name")} *`
                  : t("enter_scenario_name")
              }
              id="scenario-name"
              formControlProps={{
                fullWidth: true,
              }}
              onChange={(e) => setScenarioName(e.target.value)}
              value={scenarioName}
              inputProps={{
                placeholder: t("enter_scenario_name"),
              }}
              error={
                (formSubmitted && !scenarioName) ||
                (checkInfoCompleted && !scenarioName)
              }
              helperText={
                checkInfoCompleted && !scenarioName
                  ? t("scenario_name_required")
                  : ""
              }
              client
              noPaddingTop
            />
          </Box>
          <Box className={classes.scenarioDescriptionInput}>
            <CustomInput
              labelText={t("scenario_description")}
              id="scenario-description"
              formControlProps={{
                fullWidth: true,
              }}
              onChange={(e) => setScenarioDescription(e.target.value)}
              value={scenarioDescription}
              inputProps={{
                placeholder: t("enter_scenario_description"),
              }}
              client
              noPaddingTop
            />
          </Box>
          {availableScenarios.length > 0 && (
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              onClick={handleOpenCopyDialog}
              className={classes.usePreviousButton}
            >
              {t("use_previous_choices")}
            </Button>
          )}
        </Box>
        <Box className={classes.sectionsGrid}>
          <SectionScenarioSpec
            calculationMethod={calculationMethod}
            onCalculationMethodChange={(e) =>
              setCalculationMethod(e.target.value)
            }
            scenarioPremises={scenarioPremises}
            scenarioPremisesValue={scenarioPremisesUsed}
            onScenarioPremisesChange={(e) =>
              setScenarioPremisesUsed(e.target.value)
            }
            clientColor={clientColor}
          />
          <SectionModels
            variantsModel01={variants["model01"] || []}
            selectedVariantModel01={selectedVariants["model01"]}
            onVariantSelectModel01={(variantId) =>
              handleVariantSelect("model01", variantId)
            }
            labelModel01={
              schemaInputs["model01"]?.table_description || "Model 01"
            }
            variantsModel02={variants["model02"] || []}
            selectedVariantModel02={selectedVariants["model02"]}
            onVariantSelectModel02={(variantId) =>
              handleVariantSelect("model02", variantId)
            }
            labelModel02={
              schemaInputs["model02"]?.table_description || "Model 02"
            }
            clientColor={clientColor}
          />
        </Box>
      </Box>

      {/* Content Section - Scrollable */}
      <Box className={classes.tableSection}>
        <SectionInputVariants
          data={flattenedData}
          selectedVariants={selectedVariants}
          onSelectVariant={handleVariantSelect}
          clientColor={clientColor}
          calculationMethod={calculationMethod}
          mergeAnalysis={mergeAnalysis}
          highlightedTables={highlightedTables}
        />
      </Box>

      {/* Footer Section - Fixed */}
      <Box className={classes.footerSection}>
        {/* Buttons Row */}
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          <Box className={classes.buttonGroup}>
            <Button
              onClick={handleCheckInfo}
              className={classes.checkInfoButton}
              disabled={
                isAnalyzing || Object.keys(selectedVariants).length === 0
              }
              style={{ marginRight: "10px" }}
            >
              {isAnalyzing ? (
                <>
                  <CircularProgress
                    size={15}
                    color="inherit"
                    style={{ marginRight: "5px" }}
                  />
                  {t("analyzing")}
                </>
              ) : (
                t("check_info_button")
              )}
            </Button>
            <Button
              onClick={handleRunClick}
              className={classes.submitButton}
              disabled={isSubmitting || !scenarioName || !checkInfoCompleted}
            >
              {t("run_calculations_button")}
              {isSubmitting ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <></>
              )}
            </Button>
            <FolderSelectPopover
              open={folderPopoverOpen}
              anchorEl={runButtonAnchor}
              folders={folders}
              folderIdMap={folderIdMap}
              onCancel={handleFolderCancel}
              onConfirm={handleFolderConfirm}
              onCreateFolder={handleCreateFolder}
              isSubmitting={isSubmitting}
              clientColor={clientColor}
            />
          </Box>
        </Box>
      </Box>

      {/* Copy Scenario Dialog */}
      <DialogCopyScenario
        open={isCopyDialogOpen}
        onClose={handleCloseCopyDialog}
        scenarios={availableScenarios}
        folders={folders}
        availableVariants={variants}
        onCopySuccess={handleCopySuccess}
        isScenariosLoading={isLoading}
      />
    </Box>
  );
};

ScenarioCreationContent.propTypes = {
  onClose: PropTypes.func.isRequired,
  scenarios: PropTypes.array,
  isLoading: PropTypes.bool,
};
